import {FC, useCallback, useEffect, useRef, useState} from "react";
import { Page } from "./Page";
import { CentralContent } from "../components/central-content/CentralContent";
import { WakeletHeader } from "../components/wakelet-header/WakeletHeader";
import { IconEmail2 } from "../components/icons/icons";
import { Button, Text, Toast } from "@wakelet/ui-kit";
import { useAuth } from "../components/auth/AuthContext";
import { Navigate } from "react-router-dom";
import { FullPageSpinner } from "../components/loading/Spinner";
import { AccountsAPI } from "../accounts-api/accounts-api";
import { requiresVerification } from "../components/auth/types";
import { reloadCurrentUser } from "../firebase";
import { getWakeletUrl } from "../env/env";

export const VerifyEmailPage: FC = () => {
  const { user, loading } = useAuth();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState<boolean>(false);
  // Local UI cooldown to prevent re-sending too quickly
  const COOLDOWN_SECONDS = 30;
  const POLLING_SECONDS = 90;
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const hasSentRef = useRef(false);
  const [now, setNow] = useState<number>(Date.now());
  const remainingSeconds = lastSentAt
    ? Math.min(
          COOLDOWN_SECONDS,
          Math.max(0, COOLDOWN_SECONDS - Math.floor((now - lastSentAt) / 1000)),
      )
    : 0;
  const cooldownActive = remainingSeconds > 0;
  const pollingActive = lastSentAt && (now - lastSentAt < POLLING_SECONDS * 1000);

  const email = user?.email || "";
  const needsVerification = !!user && requiresVerification(user);

  const send = useCallback(async () => {
    if (!email) {
      setError("No email for current user");
      return;
    }
    // start cooldown immediately on send attempt (regardless of success)
    setLastSentAt(Date.now());
    setSending(true);
    setError(null);
    setResent(false);
    try {
      await AccountsAPI.triggerVerificationEmail(email);
    } catch (e) {
      setError("Unable to send verification email. Please try again.");
    } finally {
      setSending(false);
    }
  }, [email]);

  const logout = useCallback(() => {
    if (!user?.uid) {
      return;
    }

    const searchParams = new URLSearchParams({
      user_hint: user.uid,
      post_logout_redirect_uri: getWakeletUrl("/accounts/logout"),
    });

    window.location.assign(`/logout?${searchParams.toString()}`);
  }, [user?.uid]);

  const resend = async () => {
    if (cooldownActive) {
      return;
    }
    try {
      await send();
      setResent(true);
    } catch (e) {
      setError("Unable to send verification email. Please try again.");
    }
  };

  useEffect(() => {
      if (!user?.email || !needsVerification) return;
      if (hasSentRef.current) return;
      void send();
      hasSentRef.current = true;

      window.addEventListener("focus", reloadCurrentUser);
      document.addEventListener("visibilitychange", reloadCurrentUser);
      window.addEventListener("online", reloadCurrentUser);

      return () => {
        window.removeEventListener("focus", reloadCurrentUser);
        document.removeEventListener("visibilitychange", reloadCurrentUser);
        window.removeEventListener("online", reloadCurrentUser);
      };
  }, [email, needsVerification, send])

  // ticking timer to update remaining seconds while cooldown is active
  const ticking = cooldownActive || pollingActive;
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [ticking]);

  // ticking timer to reload user at regular intervals while polling is active
  useEffect(() => {
    if (!pollingActive) return;
    const id = setInterval(() => reloadCurrentUser(), 20_000);
    return () => clearInterval(id);
  }, [pollingActive]);

  if (loading) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const buttonDisabled =
    sending || !user?.email || cooldownActive || lastSentAt === null;
  const buttonText = sending
    ? "Sending..."
    : cooldownActive
      ? `Resend available in ${remainingSeconds}s`
      : "Resend email";

  return (
    <Page allowAuthUnverified>
      <CentralContent>
        <div className="flex flex-col items-center">
          <WakeletHeader message="" />
          <div className="w-[100px] h-[100px] rounded-2xl bg-[color:var(--color-decorative-green-container)] flex items-center justify-center my-6">
            <IconEmail2 className="text-[color:var(--color-decorative-green-geometry)] w-[60px] h-[60px]" />
          </div>
          <Text.Heading level={3} className="pb-1.5">
            Verify your email
          </Text.Heading>
          <Text.Label className="text-center leading-6">
            We&apos;ve just sent an email confirmation to
            <br />
            <span className="text-[color:var(--color-text-secondary)] font-bold">
              {user?.email}
            </span>
            <br />
            Click the link to verify your email.
          </Text.Label>
          <Button
            onClick={resend}
            variant="secondary"
            disabled={buttonDisabled}
            className="mt-8 max-w-[336px] w-full"
            data-testid="resend-verification-btn"
          >
            <Button.Label text={buttonText} />
          </Button>
          <Button
              onClick={reloadCurrentUser}
              variant="secondary"
              className="mt-4 max-w-[336px] w-full"
              data-testid="verified-btn"
          >
            <Button.Label text="I've verified!" />
          </Button>
          <Button
            onClick={logout}
            variant="ghost"
            className="mt-4 max-w-[336px] w-full"
            data-testid="logout-btn"
          >
            <Button.Label text="Log out" />
          </Button>
          <Toast
            type="success"
            description="Verification email re-sent. Please check your inbox."
            open={resent}
            onOpenChange={setResent}
            data-testid="resend-success-toast"
          />
          <Toast
            type="error"
            description={error || ""}
            open={!!error}
            onOpenChange={() => setError(null)}
            data-testid="resend-error-toast"
          />
        </div>
      </CentralContent>
    </Page>
  );
};
